import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "axios";

import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import MunicipalityChipStrip from "../components/preferences/MunicipalityChipStrip";
import PublicationFavouritesEditor from "../components/preferences/PublicationFavouritesEditor";
import MunicipalityProvincePicker from "../components/preferences/MunicipalityProvincePicker";

import { updateAccountInfo } from "../actions";
import { getEnvEndpoints } from "../utils/functions";
import "../assets/css/PreferencesPage.css";

const PreferencesPage = (props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { translate, lang } = useSelector((state) => state.general);
  const accountData = useSelector((state) => state.account.accountData);

  const [activeMunicipalityId, setActiveMunicipalityId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const env = getEnvEndpoints();

  useEffect(() => {
    if (!localStorage.getItem("StadGemeenteToken")) {
      history.push("/");
    }
  }, [history]);

  const refreshAccountInfo = useCallback(async () => {
    const token = localStorage.getItem("StadGemeenteToken");
    if (!token) return null;
    setRefreshing(true);
    try {
      const response = await axios.get(
        `${env.REACT_APP_BASE_MEDIA_URL}/core/api/stadgemeente/user/?lang=${lang}`,
        { headers: { Authorization: `Token ${token}` } }
      );
      dispatch(updateAccountInfo(response.data));
      return response.data;
    } catch (err) {
      return null;
    } finally {
      setRefreshing(false);
    }
  }, [env.REACT_APP_BASE_MEDIA_URL, lang, dispatch]);

  useEffect(() => {
    if (!accountData || activeMunicipalityId) return;
    if (accountData.home_municipality?.id) {
      setActiveMunicipalityId(accountData.home_municipality.id);
    } else if (accountData.followed_municipalities?.length > 0) {
      setActiveMunicipalityId(accountData.followed_municipalities[0].id);
    }
  }, [accountData, activeMunicipalityId]);

  const isLoading = !accountData && refreshing;
  const followedMunicipalities = accountData?.followed_municipalities || [];
  const homeMunicipality = accountData?.home_municipality || null;
  const pickerState = accountData?.picker_state || { municipality_ids: [], province_ids: [] };

  return (
    <div className="preferences-page">
      <Navigation type="municipality" />
      <div className="preferences-container">
        <h1>{translate("my_preferences") || "Mijn voorkeuren"}</h1>
        <p className="subtitle">
          {translate("preferences_subtitle") ||
            "Beheer welke gemeenten u volgt en kies welke onderwerpen u wilt ontvangen."}
        </p>

        {isLoading && (
          <div className="preferences-loading">
            {translate("loading") || "Laden..."}
          </div>
        )}

        {!isLoading && accountData && (
          <>
            <section className="preferences-section preferences-section--chips">
              <h2>{translate("my_followed_municipalities") || "Mijn gevolgde gemeenten"}</h2>
              <p className="section-help">
                {translate("my_followed_municipalities_help") ||
                  "Klik op een gemeente om favorieten te beheren."}
              </p>
              <MunicipalityChipStrip
                municipalities={followedMunicipalities}
                homeId={homeMunicipality?.id}
                activeId={activeMunicipalityId}
                onSelect={setActiveMunicipalityId}
                translate={translate}
              />
            </section>

            <section className="preferences-section preferences-section--editor">
              <PublicationFavouritesEditor
                municipalityId={activeMunicipalityId}
                lang={lang}
                translate={translate}
                createNotification={props.createNotification}
              />
            </section>

            <section className="preferences-section preferences-section--picker">
              <h2>
                {translate("add_extra_municipalities_provinces") ||
                  "Extra gemeenten of provincies toevoegen"}
              </h2>
              <p className="section-help">
                {translate("add_extra_municipalities_provinces_help") ||
                  "Hier kunt u extra gemeenten of hele provincies volgen of verwijderen."}
              </p>
              <MunicipalityProvincePicker
                selectedMunicipalityIds={pickerState.municipality_ids}
                selectedProvinceIds={pickerState.province_ids}
                homeMunicipalityId={homeMunicipality?.id}
                lang={lang}
                translate={translate}
                onSaved={async () => {
                  const fresh = await refreshAccountInfo();
                  if (fresh) {
                    const stillFollowed = fresh.followed_municipalities?.some(
                      (m) => m.id === activeMunicipalityId
                    );
                    if (!stillFollowed && fresh.home_municipality?.id) {
                      setActiveMunicipalityId(fresh.home_municipality.id);
                    }
                  }
                }}
                createNotification={props.createNotification}
              />
            </section>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PreferencesPage;
